import type { LucideIcon } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { Tag, type TagShape, type TagTone } from "./Tag";

export type StatusBadgeType = "beta" | "coming-soon";
export type StatusBadgeTone = Exclude<
  TagTone,
  "sourceBlue" | "sourcePurple" | "sourceGreen"
>;
export type StatusBadgeShape = TagShape;

const I18N_KEY: Record<StatusBadgeType, string> = {
  beta: "beta",
  "coming-soon": "coming_soon",
};

type BadgeBaseProps = {
  tone?: StatusBadgeTone;
  icon?: LucideIcon;
  shape?: StatusBadgeShape;
  iconOnly?: boolean;
  accessibilityLabel?: string;
};

type Props = BadgeBaseProps &
  (
    | {
        type: StatusBadgeType;
        label?: never;
      }
    | {
        type?: never;
        label: string;
      }
  );

export function StatusBadge({
  type,
  label,
  tone = "neutral",
  icon: Icon,
  shape = "rounded",
  iconOnly = false,
  accessibilityLabel,
}: Props) {
  const t = useTranslations();
  const displayLabel =
    label ?? (type ? t(`status_badge.${I18N_KEY[type]}`) : "");

  return (
    <Tag
      accessibilityLabel={accessibilityLabel ?? displayLabel}
      icon={Icon}
      iconOnly={iconOnly}
      label={displayLabel}
      shape={shape}
      tone={tone}
    />
  );
}

export default StatusBadge;
